import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Plus,
  Upload,
  Image as ImageIcon,
  X,
  CalendarDays,
  ListChecks,
  Trash2,
  Pencil,
  Download,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Clock,
  MapPin,
} from "lucide-react";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function parseYMD(s) {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}
function formatLongDate(s) {
  const d = parseYMD(s);
  if (!d) return s || "";
  return d.toLocaleDateString(undefined, { weekday: "short", month: "long", day: "numeric", year: "numeric" });
}
function formatTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hh = parseInt(h, 10);
  const ampm = hh >= 12 ? "PM" : "AM";
  const h12 = ((hh + 11) % 12) + 1;
  return `${h12}:${m} ${ampm}`;
}
function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function addMonths(d, n) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
function buildMonthGrid(viewDate) {
  const first = startOfMonth(viewDate);
  const firstWeekday = (first.getDay() + 6) % 7; // 0=Mon
  const start = new Date(first);
  start.setDate(first.getDate() - firstWeekday);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push(d);
  }
  return cells;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const r = reader.result || "";
      const idx = String(r).indexOf("base64,");
      resolve(idx >= 0 ? String(r).slice(idx + 7) : "");
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const emptyEvent = { title: "", event_date: ymd(new Date()), event_time: "", end_time: "", location: "", notes: "" };

function EventDialog({ open, onOpenChange, initial, onSave, source = "manual" }) {
  const [form, setForm] = useState(emptyEvent);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ ...emptyEvent, ...(initial || {}) });
  }, [open, initial]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.event_date) {
      toast.error("Title and date are required.");
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const isReview = source === "flyer";
  const isEdit = !!initial?.id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="event-dialog">
        <DialogHeader>
          <DialogTitle>
            {isReview ? "Review extracted details" : isEdit ? "Edit event" : "Add a campaign event"}
          </DialogTitle>
          <DialogDescription>
            {isReview ? (
              <span>
                We pulled these from your flyer photo. <span className="font-semibold text-flag-navy">Please double-check every field</span> — image-to-text can misread dates and times.
              </span>
            ) : (
              "Meet & greets, fundraisers, debates — anything happening on the trail."
            )}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-sm font-semibold text-slate-800">Event title *</Label>
            <Input
              required
              value={form.title}
              onChange={(e) => setF("title", e.target.value)}
              maxLength={200}
              placeholder='e.g. "Lincoln Day Dinner"'
              className="mt-1 h-11 text-base"
              data-testid="event-title-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-semibold text-slate-800">Date *</Label>
              <Input
                type="date"
                required
                value={form.event_date}
                onChange={(e) => setF("event_date", e.target.value)}
                className="mt-1 h-11 text-base"
                data-testid="event-date-input"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-800">Start time</Label>
              <Input
                type="time"
                value={form.event_time}
                onChange={(e) => setF("event_time", e.target.value)}
                className="mt-1 h-11 text-base"
                data-testid="event-time-input"
              />
            </div>
          </div>
          <div>
            <Label className="text-sm font-semibold text-slate-800">End time (optional)</Label>
            <Input
              type="time"
              value={form.end_time}
              onChange={(e) => setF("end_time", e.target.value)}
              className="mt-1 h-11 text-base"
              data-testid="event-end-time-input"
            />
          </div>
          <div>
            <Label className="text-sm font-semibold text-slate-800">Location</Label>
            <Input
              value={form.location}
              onChange={(e) => setF("location", e.target.value)}
              maxLength={300}
              placeholder="Venue name, address"
              className="mt-1 h-11 text-base"
              data-testid="event-location-input"
            />
          </div>
          <div>
            <Label className="text-sm font-semibold text-slate-800">Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setF("notes", e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="Hosts, RSVP info, dress code, tickets, sponsors…"
              className="mt-1 text-base"
              data-testid="event-notes-input"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              data-testid="event-cancel-btn"
              className="font-semibold"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="event-save-btn"
              className="bg-flag-red hover:bg-flag-red-hover text-white font-bold"
            >
              {saving ? "Saving…" : isReview ? "Confirm & save" : isEdit ? "Save changes" : "Add event"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FlyerUploadDialog({ open, onOpenChange, onExtracted }) {
  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setPreview("");
      setLoading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }, [open]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Please use JPG, PNG, or WEBP.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image must be under 8MB.");
      return;
    }
    setPreview(URL.createObjectURL(file));
    setLoading(true);
    try {
      const b64 = await fileToBase64(file);
      const { data } = await api.post("/campaign-events/extract", {
        image_base64: b64,
        image_mime: file.type,
      });
      const anyField = (data.title || data.event_date || data.event_time || data.location || data.notes || "").trim();
      if (!anyField) {
        toast.error("We couldn't pick up clear details from that image. Try a sharper photo or add the event manually.");
        setLoading(false);
        return;
      }
      onExtracted(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="flyer-dialog">
        <DialogHeader>
          <DialogTitle>Read a flyer or invite</DialogTitle>
          <DialogDescription>
            Snap a photo of a flyer, invite, or event graphic. We'll pull out the title, date, time, and location for you to review before saving.
            <span className="block mt-2 text-flag-red font-semibold">
              We never save automatically — image text can be misread, so you'll always confirm the details first.
            </span>
          </DialogDescription>
        </DialogHeader>
        {preview ? (
          <div className="space-y-3">
            <img src={preview} alt="Flyer" className="w-full max-h-64 object-contain rounded-lg border border-slate-200" data-testid="flyer-preview" />
            <div className="text-sm text-slate-600 flex items-center gap-2" data-testid="flyer-loading">
              <Sparkles className="h-4 w-4 text-flag-red" />
              {loading ? "Reading the flyer…" : "Done."}
            </div>
          </div>
        ) : (
          <label
            htmlFor="flyer-upload"
            data-testid="flyer-upload-label"
            className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 hover:border-flag-navy rounded-lg px-4 py-10 cursor-pointer transition-colors"
          >
            <ImageIcon className="h-7 w-7 text-slate-500" />
            <span className="text-sm font-semibold text-slate-700">Click to upload a flyer photo</span>
            <span className="text-xs text-slate-500">JPG · PNG · WEBP · up to 8MB</span>
            <input
              id="flyer-upload"
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={onPick}
              className="hidden"
              data-testid="flyer-upload-input"
            />
          </label>
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="flyer-cancel-btn"
            className="font-semibold"
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CampaignCalendarPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [viewDate, setViewDate] = useState(startOfMonth(new Date()));
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [flyerDialogOpen, setFlyerDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [source, setSource] = useState("manual");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/campaign-events");
      setEvents(data || []);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const eventsByDate = useMemo(() => {
    const m = {};
    for (const e of events) (m[e.event_date] = m[e.event_date] || []).push(e);
    return m;
  }, [events]);
  const upcoming = useMemo(() => {
    const today = ymd(new Date());
    return events.filter((e) => e.event_date >= today);
  }, [events]);
  const past = useMemo(() => {
    const today = ymd(new Date());
    return events.filter((e) => e.event_date < today);
  }, [events]);

  const openManualAdd = () => {
    setSource("manual");
    setEditing(null);
    setEventDialogOpen(true);
  };

  const openFlyerUpload = () => {
    setFlyerDialogOpen(true);
  };

  const handleExtracted = (data) => {
    setSource("flyer");
    setEditing({
      title: data.title || "",
      event_date: data.event_date || ymd(new Date()),
      event_time: data.event_time || "",
      end_time: "",
      location: data.location || "",
      notes: data.notes || "",
    });
    setFlyerDialogOpen(false);
    setEventDialogOpen(true);
  };

  const saveEvent = async (payload) => {
    if (editing?.id) {
      await api.put(`/campaign-events/${editing.id}`, payload);
    } else {
      await api.post("/campaign-events", payload);
    }
    setEditing(null);
    setSource("manual");
    await load();
    toast.success(editing?.id ? "Event updated" : "Event added");
  };

  const removeEvent = async (id) => {
    try {
      await api.delete(`/campaign-events/${id}`);
      setEvents((prev) => prev.filter((x) => x.id !== id));
      toast.success("Removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const downloadIcs = async (event) => {
    try {
      const res = await api.get(`/campaign-events/${event.id}/ics`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/calendar;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (event.title || "event").replace(/[^A-Za-z0-9_-]+/g, "-").slice(0, 40) || "event";
      a.download = `${safe}.ics`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Calendar file downloaded. Open it to add to your calendar.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const cells = useMemo(() => buildMonthGrid(viewDate), [viewDate]);
  const todayStr = ymd(new Date());

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wider text-flag-red">Campaign Calendar</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-flag-navy mt-1">Your trail, on a calendar</h1>
          <p className="text-slate-600 mt-2">Real-world events — meet &amp; greets, fundraisers, debates, parades. Add them by hand, or snap a photo of a flyer.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={openFlyerUpload}
            data-testid="event-flyer-btn"
            variant="outline"
            className="h-11 px-4 border-2 border-flag-navy text-flag-navy hover:bg-flag-navy hover:text-white font-bold"
          >
            <Upload className="h-4 w-4 mr-1.5" /> Read a flyer
          </Button>
          <Button
            onClick={openManualAdd}
            data-testid="event-add-btn"
            className="h-11 px-5 bg-flag-red hover:bg-flag-red-hover text-white font-bold"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Add event
          </Button>
        </div>
      </div>

      <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 px-4 py-3 text-sm flex gap-2" data-testid="campaign-cal-disclaimer">
        <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <div>
          <span className="font-semibold">A note on election deadlines.</span> Registration, early-voting, and mail-in-ballot dates vary by jurisdiction and change. Don't take them from a flyer — look them up at your local board of elections and enter them yourself.
        </div>
      </div>

      {error && <div className="mt-4 text-sm text-flag-red bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}

      <Tabs defaultValue="upcoming" className="mt-8">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="upcoming" data-testid="campaign-cal-tab-upcoming"><ListChecks className="h-4 w-4 mr-1.5" /> Upcoming</TabsTrigger>
          <TabsTrigger value="month" data-testid="campaign-cal-tab-month"><CalendarDays className="h-4 w-4 mr-1.5" /> Month</TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming" className="mt-6">
          {loading ? (
            <div className="text-slate-500">Loading…</div>
          ) : upcoming.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center" data-testid="campaign-cal-empty">
              <p className="text-slate-600">Nothing on the calendar yet.</p>
              <p className="text-slate-500 text-sm mt-1">Add your first event or upload a flyer photo to get started.</p>
            </div>
          ) : (
            <ul className="space-y-3" data-testid="campaign-cal-upcoming-list">
              {upcoming.map((e) => (
                <EventRow
                  key={e.id}
                  event={e}
                  onEdit={() => { setEditing(e); setSource("manual"); setEventDialogOpen(true); }}
                  onDelete={removeEvent}
                  onIcs={downloadIcs}
                />
              ))}
            </ul>
          )}

          {past.length > 0 && (
            <details className="mt-8" data-testid="campaign-cal-past-details">
              <summary className="cursor-pointer text-sm font-semibold text-slate-500 hover:text-flag-navy">
                Past events ({past.length})
              </summary>
              <ul className="mt-3 space-y-3 opacity-70">
                {past.map((e) => (
                  <EventRow key={e.id} event={e} onEdit={() => { setEditing(e); setSource("manual"); setEventDialogOpen(true); }} onDelete={removeEvent} onIcs={downloadIcs} />
                ))}
              </ul>
            </details>
          )}
        </TabsContent>

        <TabsContent value="month" className="mt-6">
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 sm:p-6">
            <div className="flex items-center justify-between gap-2">
              <button
                onClick={() => setViewDate((d) => addMonths(d, -1))}
                className="h-9 w-9 rounded-md border border-slate-200 hover:bg-slate-50 flex items-center justify-center"
                data-testid="campaign-cal-prev-month"
                aria-label="Previous month"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <div className="font-display text-xl font-bold text-flag-navy" data-testid="campaign-cal-month-label">
                {viewDate.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setViewDate(startOfMonth(new Date()))}
                  className="h-9 px-3 text-sm font-semibold rounded-md border border-slate-200 hover:bg-slate-50"
                  data-testid="campaign-cal-today-btn"
                >
                  Today
                </button>
                <button
                  onClick={() => setViewDate((d) => addMonths(d, 1))}
                  className="h-9 w-9 rounded-md border border-slate-200 hover:bg-slate-50 flex items-center justify-center"
                  data-testid="campaign-cal-next-month"
                  aria-label="Next month"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-7 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden text-xs sm:text-sm">
              {WEEKDAYS.map((w) => (
                <div key={w} className="bg-slate-50 px-2 py-2 font-bold text-slate-600 text-center">{w}</div>
              ))}
              {cells.map((d) => {
                const dStr = ymd(d);
                const inMonth = d.getMonth() === viewDate.getMonth();
                const isToday = dStr === todayStr;
                const dayEvents = eventsByDate[dStr] || [];
                return (
                  <div
                    key={dStr}
                    className={`min-h-[88px] sm:min-h-[110px] bg-white p-1.5 flex flex-col ${inMonth ? "" : "bg-slate-50/60 text-slate-400"}`}
                    data-testid={`campaign-cal-cell-${dStr}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-bold ${isToday ? "inline-flex h-5 w-5 items-center justify-center rounded-full bg-flag-red text-white" : ""}`}>
                        {d.getDate()}
                      </span>
                    </div>
                    <div className="mt-1 space-y-1 overflow-hidden">
                      {dayEvents.slice(0, 3).map((e) => (
                        <button
                          key={e.id}
                          onClick={() => { setEditing(e); setSource("manual"); setEventDialogOpen(true); }}
                          title={e.title}
                          data-testid={`campaign-cal-cell-event-${e.id}`}
                          className="block w-full text-left px-1.5 py-0.5 rounded text-[11px] sm:text-xs leading-tight font-semibold truncate bg-flag-red/10 text-flag-red hover:bg-flag-red/15 transition-colors"
                        >
                          {e.event_time ? `${formatTime(e.event_time)} · ` : ""}{e.title}
                        </button>
                      ))}
                      {dayEvents.length > 3 && (
                        <div className="text-[10px] text-slate-500 font-semibold px-1.5">+{dayEvents.length - 3} more</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <EventDialog
        open={eventDialogOpen}
        onOpenChange={(v) => { setEventDialogOpen(v); if (!v) { setEditing(null); setSource("manual"); } }}
        initial={editing}
        onSave={saveEvent}
        source={source}
      />
      <FlyerUploadDialog
        open={flyerDialogOpen}
        onOpenChange={setFlyerDialogOpen}
        onExtracted={handleExtracted}
      />
    </div>
  );
}

function EventRow({ event, onEdit, onDelete, onIcs }) {
  return (
    <li
      className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 sm:p-5 flex flex-wrap items-start justify-between gap-3"
      data-testid={`campaign-cal-event-row-${event.id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{formatLongDate(event.event_date)}</div>
        <div className="mt-1 font-display text-lg font-bold text-flag-navy">{event.title}</div>
        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-700">
          {event.event_time && (
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {formatTime(event.event_time)}{event.end_time ? ` – ${formatTime(event.end_time)}` : ""}
            </span>
          )}
          {event.location && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" /> {event.location}
            </span>
          )}
        </div>
        {event.notes && <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap leading-relaxed">{event.notes}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          onClick={() => onIcs(event)}
          data-testid={`campaign-cal-ics-${event.id}`}
          className="h-9 px-3 bg-flag-navy hover:bg-flag-navy-2 text-white font-bold"
        >
          <Download className="h-3.5 w-3.5 mr-1.5" /> Add to my calendar
        </Button>
        <Button
          variant="outline"
          onClick={onEdit}
          data-testid={`campaign-cal-edit-${event.id}`}
          className="h-9 px-3 border-2 font-semibold"
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              data-testid={`campaign-cal-delete-${event.id}`}
              className="h-9 px-3 text-slate-600 hover:text-flag-red hover:bg-red-50 font-semibold"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this event?</AlertDialogTitle>
              <AlertDialogDescription>It will be permanently removed from your campaign calendar.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid={`campaign-cal-delete-cancel-${event.id}`}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => onDelete(event.id)}
                data-testid={`campaign-cal-delete-confirm-${event.id}`}
                className="bg-flag-red hover:bg-flag-red-hover"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </li>
  );
}
